import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import { validationMessages } from 'constants/validation'
import {
	Stack, StackProps,	
	Rating,
	TextField,
	Autocomplete,
	Box
} from '@mui/material'

import { Section } from 'components/controls/section'

import { SearchsConventionFormData } from 'interface/search/convention'
import { useListConvention } from 'interface/convention'
import { Loading } from 'components/controls/loading'

const rateList = [
  { rate: 0.2, value: 1, description: '星1' },
  { rate: 0.5, value: 2, description: '星2' },
  { rate: 1.0, value: 3, description: '星3' },
  { rate: 2.0, value: 4, description: '星4' },
  { rate: 3.0, value: 5, description: '星5' },
]

const {
  Required
} = validationMessages

const formSchema = yup.object().shape({
  conventionId: yup.number().required(Required).min(1, Required),
	rate: yup.number().required(Required)
})

type useSearchsConventionFormProps = {
	searchId: number
	data?: SearchsConventionFormData
	onSubmit: (data: SearchsConventionFormData) => void
}
export const useSearchsConventionForm = ({
	data,
	onSubmit
}: useSearchsConventionFormProps) => {
	const conventionListState = useListConvention()
	const form = useForm({
		defaultValues: data ? {
			conventionId: data.conventionId,
			rate: data.rate ?? 1
		} : {
			conventionId: 0,
			rate: 1
		},
		mode: 'onChange',
		resolver: yupResolver(formSchema)
	})
	return {
		...form,
		conventionListState,
		submit: form.handleSubmit(input => onSubmit({
			conventionId: input.conventionId,
			rate: input.rate,
			updateTime: data?.updateTime
		}))
	}
}

export const SearchsConventionForm = ({
	control,
	setValue,
	formState: { errors },
	conventionListState,
	...others
}: ReturnType<typeof useSearchsConventionForm> & StackProps) => {
	return (
		<>
      { conventionListState.loading && <Loading /> }

			<Stack direction="column" {...others}>
				<Section label='学会' labelProps={{ paddingY: 2 }}>
					<Controller
						control={control}
						name="conventionId"
						render={({ field }) => (
							<Autocomplete<number | null>
								{...field}
								size='small'
								options={conventionListState.data?.list.map(master => master.conventionId) || []}
								getOptionLabel={
									id => conventionListState.data?.list.find(
										master => master.conventionId === id
									)?.conventionName ?? ''
								}
								renderInput={(params) => (
									<TextField
										{...params}
										error={!!errors.conventionId}
										helperText={errors.conventionId ? errors.conventionId.message : ''}
									/>
								)}
								renderOption={(props, id) => {
									const record = conventionListState.data?.list.find(master => master.conventionId === id)
									return record ? (
										<Box component="li" {...props}>
											{`${record.numberOfTimes} ${record.conventionName}`}
										</Box>
									) : <></>
								}}
								onChange={(_, value) => {
									setValue("conventionId", value ?? 0, { shouldValidate: true })
								}}
							/>
						)}
					/>
				</Section>

				<Section label="重要度" labelProps={{ paddingY: 2 }}>
					<Controller
						control={control}
						name="rate"
						render={({ field }) => (
							<Rating
								value={rateList.find(r => r.rate === field.value)?.value ?? 0}
								name="rate"
								onChange={(e, v) => field.onChange(e, rateList.find(r => r.value === v)?.rate ?? 10)}
							/>
						)}
					/>
				</Section>
			</Stack>
		</>
	)
}